import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   SpecificationAnalysisDetailsPanel.jsx
   ----------------------------------------------------------------------------
   ZONE B2 — NAVIGATEUR ANALYTIQUE

   RÔLE
   - cadrer l’analyse
   - proposer des axes de lecture métier
   - guider l’utilisateur dans le corpus sans exposer la mécanique interne

   STRUCTURE
   0. Navigator synthétique
   1. Axe d’analyse
   2. Périmètre analysé
   3. Famille analysée
============================================================================ */

/* ============================================================================
   TOKENS
============================================================================ */

const T = {
  bg: theme?.colors?.background?.surface || "#ffffff",
  hover: theme?.colors?.background?.hover || "#f7f9fc",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",

  text: theme?.colors?.text?.primary || "#164D63",
  body: theme?.colors?.text?.body || "#3F5D6E",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  corelia: theme?.colors?.text?.corelia || "#0e3f56",

  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",
  red: theme?.Generic?.red?.dark?.bg || "#C2551A",
  blue: theme?.colors?.text?.primary || "#164D63",

  pastelBlue: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  pastelGreen: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  pastelYellow: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  pastelGrey: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",
};

/* ============================================================================
   DATA MOCK
============================================================================ */

const ANALYSIS_TREE = {
  axes: [
    { id: "ouvrages", label: "Ouvrages", count: 4, tone: "blue" },
    { id: "systemes", label: "Systèmes", count: 6, tone: "blue" },
    { id: "lots", label: "Lots", count: 4, tone: "orange" },
    { id: "disciplines", label: "Disciplines", count: 5, tone: "green" },
    { id: "interfaces", label: "Interfaces", count: 4, tone: "neutral" },
  ],

  branchesByAxis: {
    ouvrages: [
      { id: "station", label: "Station de pompage", meta: "46 specs" },
      { id: "reservoir", label: "Réservoir", meta: "31 specs" },
      { id: "reseau", label: "Réseau AEP", meta: "58 specs" },
      { id: "batiment", label: "Bâtiment technique", meta: "22 specs" },
    ],

    systemes: [
      { id: "pompage", label: "Pompage", meta: "18 specs" },
      { id: "commande", label: "Commande", meta: "12 specs" },
      { id: "instrumentation", label: "Instrumentation", meta: "9 specs" },
      { id: "structure", label: "Structure", meta: "15 specs" },
      { id: "drainage", label: "Drainage", meta: "8 specs" },
      { id: "exploitation", label: "Exploitation", meta: "11 specs" },
    ],

    lots: [
      { id: "lot-gc", label: "Lot Génie civil", meta: "39 specs" },
      { id: "lot-hyd", label: "Lot Hydraulique", meta: "44 specs" },
      { id: "lot-ele", label: "Lot Électrique", meta: "24 specs" },
      { id: "lot-exp", label: "Lot Exploitation", meta: "15 specs" },
    ],

    disciplines: [
      { id: "hyd", label: "Hydraulique", meta: "54 specs" },
      { id: "ele", label: "Électricité", meta: "28 specs" },
      { id: "str", label: "Structure", meta: "19 specs" },
      { id: "gc", label: "Génie civil", meta: "39 specs" },
      { id: "aut", label: "Automatisme", meta: "17 specs" },
    ],

    interfaces: [
      { id: "hyd-ele", label: "Hydraulique / Électricité", meta: "12 cas" },
      { id: "gc-hyd", label: "GC / Hydraulique", meta: "9 cas" },
      { id: "ele-aut", label: "Électricité / Automatisme", meta: "8 cas" },
      { id: "exp-main", label: "Exploitation / Maintenance", meta: "7 cas" },
    ],
  },

  familiesByBranch: {
    station: [
      { id: "performance", label: "Performance hydraulique", hint: "8 specs" },
      { id: "continuite", label: "Continuité de service", hint: "10 specs" },
      { id: "securite", label: "Sécurité exploitation", hint: "7 specs" },
      { id: "maintenance", label: "Maintenance", hint: "9 specs" },
    ],
    reservoir: [
      { id: "durabilite", label: "Durabilité", hint: "7 specs" },
      { id: "protection", label: "Protection surface", hint: "4 specs" },
      { id: "drainage-res", label: "Drainage", hint: "5 specs" },
    ],
    reseau: [
      { id: "pression", label: "Pression", hint: "14 specs" },
      { id: "trace", label: "Tracé", hint: "16 specs" },
      { id: "maintenance-reseau", label: "Maintenance", hint: "8 specs" },
    ],
    batiment: [
      { id: "circulations", label: "Circulations", hint: "5 specs" },
      { id: "enveloppe", label: "Enveloppe", hint: "8 specs" },
      { id: "maintenance-bat", label: "Maintenance", hint: "9 specs" },
    ],

    pompage: [
      { id: "perf-pompage", label: "Performance", hint: "6 specs" },
      { id: "service-pompage", label: "Continuité de service", hint: "5 specs" },
      { id: "maint-pompage", label: "Maintenance", hint: "7 specs" },
    ],
    commande: [
      { id: "logique-commande", label: "Logique de commande", hint: "4 specs" },
      { id: "retour-etat", label: "Retour d’état", hint: "4 specs" },
      { id: "secours-commande", label: "Secours", hint: "4 specs" },
    ],
    instrumentation: [
      { id: "mesure", label: "Mesure", hint: "3 specs" },
      { id: "transmission", label: "Transmission", hint: "3 specs" },
      { id: "maintenance-inst", label: "Maintenance", hint: "3 specs" },
    ],
    structure: [
      { id: "durabilite-struct", label: "Durabilité", hint: "6 specs" },
      { id: "protection-struct", label: "Protection surface", hint: "5 specs" },
      { id: "interfaces-struct", label: "Interfaces", hint: "4 specs" },
    ],
    drainage: [
      { id: "collecte", label: "Collecte", hint: "3 specs" },
      { id: "evacuation", label: "Évacuation", hint: "3 specs" },
      { id: "protection-drain", label: "Protection ouvrage", hint: "2 specs" },
    ],
    exploitation: [
      { id: "acces", label: "Accès", hint: "4 specs" },
      { id: "routine", label: "Routine d’entretien", hint: "3 specs" },
      { id: "securite-exp", label: "Sécurité", hint: "4 specs" },
    ],

    "lot-gc": [
      { id: "fond-gc", label: "Fondations", hint: "10 specs" },
      { id: "massifs-gc", label: "Massifs", hint: "8 specs" },
      { id: "reservations-gc", label: "Réservations", hint: "4 specs" },
    ],
    "lot-hyd": [
      { id: "pompes-hyd", label: "Pompes", hint: "12 specs" },
      { id: "service-hyd", label: "Continuité de service", hint: "10 specs" },
      { id: "maint-hyd", label: "Maintenance", hint: "8 specs" },
    ],
    "lot-ele": [
      { id: "protections-ele", label: "Protections", hint: "7 specs" },
      { id: "armoires-ele", label: "Armoires", hint: "8 specs" },
      { id: "commande-ele", label: "Commande", hint: "9 specs" },
    ],
    "lot-exp": [
      { id: "acces-exp", label: "Accès", hint: "4 specs" },
      { id: "maintenance-exp", label: "Maintenance", hint: "6 specs" },
      { id: "securite-exp-lot", label: "Sécurité", hint: "5 specs" },
    ],

    hyd: [
      { id: "perf-hyd", label: "Performance", hint: "18 specs" },
      { id: "service-hyd-disc", label: "Continuité de service", hint: "10 specs" },
      { id: "maint-hyd-disc", label: "Maintenance", hint: "18 specs" },
    ],
    ele: [
      { id: "alim-ele", label: "Alimentation", hint: "8 specs" },
      { id: "prot-ele", label: "Protection", hint: "7 specs" },
      { id: "commande-ele-disc", label: "Commande", hint: "4 specs" },
    ],
    str: [
      { id: "dur-str", label: "Durabilité", hint: "6 specs" },
      { id: "surface-str", label: "Protection surface", hint: "6 specs" },
      { id: "interfaces-str", label: "Interfaces", hint: "7 specs" },
    ],
    gc: [
      { id: "fond-gc-disc", label: "Fondations", hint: "10 specs" },
      { id: "massif-gc-disc", label: "Massifs", hint: "8 specs" },
      { id: "drain-gc-disc", label: "Drainage", hint: "8 specs" },
    ],
    aut: [
      { id: "retour-aut", label: "Retour d’état", hint: "5 specs" },
      { id: "logique-aut", label: "Logique commande", hint: "6 specs" },
      { id: "secours-aut", label: "Secours", hint: "6 specs" },
    ],

    "hyd-ele": [
      { id: "pompe-armoire", label: "Pompe / armoire", hint: "4 points" },
      { id: "commande-signal", label: "Commande / signal", hint: "3 points" },
      { id: "protection-service", label: "Protection / service", hint: "5 points" },
    ],
    "gc-hyd": [
      { id: "reservations-gchyd", label: "Réservations", hint: "4 points" },
      { id: "massifs-gchyd", label: "Massifs / support", hint: "3 points" },
      { id: "drainage-gchyd", label: "Drainage", hint: "2 points" },
    ],
    "ele-aut": [
      { id: "dialogue-eleaut", label: "Dialogue commande", hint: "5 points" },
      { id: "retour-eleaut", label: "Retour d’état", hint: "3 points" },
      { id: "secours-eleaut", label: "Secours", hint: "2 points" },
    ],
    "exp-main": [
      { id: "acces-expmain", label: "Accessibilité", hint: "3 points" },
      { id: "routine-expmain", label: "Routine d’entretien", hint: "4 points" },
      { id: "securite-expmain", label: "Sécurité usage", hint: "2 points" },
    ],
  },
};

/* ============================================================================
   HELPERS
============================================================================ */

const extractLeadingNumber = (value) => {
  if (typeof value !== "string") return 0;
  const match = value.match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const buildAggregateLabel = (items, field, fallbackUnit = "") => {
  const total = items.reduce(
    (acc, item) => acc + extractLeadingNumber(item?.[field]),
    0
  );
  if (total <= 0) return "";
  return fallbackUnit ? `${total} ${fallbackUnit}` : String(total);
};

const getAxisTone = (tone) => {
  const map = {
    blue: {
      bg: T.pastelBlue,
      text: T.blue,
    },
    green: {
      bg: T.pastelGreen,
      text: T.green,
    },
    orange: {
      bg: T.pastelYellow,
      text: T.orange,
    },
    neutral: {
      bg: T.pastelGrey,
      text: T.body,
    },
  };
  return map[tone] || map.neutral;
};

/* ============================================================================
   UI ATOMS
============================================================================ */

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: theme?.fontSizes?.sm || "0.90rem",
      fontWeight: theme?.fontWeights?.normal || 500,
      lineHeight: 1.5,
      color: T.soft,
      marginBottom: 8,
      
    }}
  >
    {children}
  </div>
);

const NavigatorLine = ({ label, level = 0 }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      paddingLeft: level * 18,
    }}
  >
    <div
      style={{
        width: 6,
        height: 6,
        background: T.corelia,
        borderRadius: 1,
        flexShrink: 0,
        marginTop: 1,
      }}
    />
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.78rem",
        lineHeight: 1.3,
        color: T.body,
      }}
    >
      {label}
    </div>
  </div>
);

const SelectRow = ({ active, label, meta, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      border: `1px solid ${active ? T.borderStrong : T.border}`,
      background: active ? "#f6fafc" : "#ffffff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "7px 8px",
      textAlign: "left",
      fontFamily: theme?.fonts?.body || "inherit",
      boxShadow: active ? `inset 2px 0 0 ${T.corelia}` : "none",
    }}
  >
    <div
      style={{
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingRight: 8,
        fontSize: "0.78rem",
        lineHeight: 1.15,
        color: T.body,
        fontWeight: 500,
      }}
    >
      {label}
    </div>

    {meta ? (
      <div
        style={{
          borderLeft: `1px solid ${T.border}`,
          paddingLeft: 6,
          marginLeft: 6,
          fontSize: "0.71rem",
          color: T.soft,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {meta}
      </div>
    ) : null}
  </button>
);

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationAnalysisDetailsPanel = ({ onSelectionChange }) => {
  const axes = ANALYSIS_TREE.axes;
  const defaultAxisId = axes[0]?.id || "all";

  const [selectedAxis, setSelectedAxis] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedFamily, setSelectedFamily] = useState("all");

  const effectiveAxis = selectedAxis === "all" ? defaultAxisId : selectedAxis;

  const branches = useMemo(() => {
    return ANALYSIS_TREE.branchesByAxis[effectiveAxis] || [];
  }, [effectiveAxis]);

  const defaultBranchId = branches[0]?.id || "all";
  const effectiveBranch =
    selectedBranch === "all" ? defaultBranchId : selectedBranch;

  const families = useMemo(() => {
    return ANALYSIS_TREE.familiesByBranch[effectiveBranch] || [];
  }, [effectiveBranch]);

  const axesWithAll = useMemo(() => {
    const total = axes.reduce((acc, item) => acc + (item.count || 0), 0);
    return [{ id: "all", label: "Vue globale", count: total, tone: "neutral" }, ...axes];
  }, [axes]);

  const branchesWithAll = useMemo(() => {
    const allMeta = buildAggregateLabel(branches, "meta", "specs");
    return [{ id: "all", label: "Tout le périmètre", meta: allMeta }, ...branches];
  }, [branches]);

  const familiesWithAll = useMemo(() => {
    const allHint = buildAggregateLabel(families, "hint", "specs");
    return [{ id: "all", label: "Toutes les familles", hint: allHint }, ...families];
  }, [families]);

  useEffect(() => {
    setSelectedBranch("all");
  }, [selectedAxis]);

  useEffect(() => {
    setSelectedFamily("all");
  }, [selectedBranch]);

  useEffect(() => {
    if (!onSelectionChange) return;

    const axisLabel =
      selectedAxis === "all"
        ? "Vue globale"
        : axes.find((item) => item.id === selectedAxis)?.label || null;

    const branchLabel =
      selectedBranch === "all"
        ? "Tout le périmètre"
        : branches.find((item) => item.id === selectedBranch)?.label || null;

    const familyLabel =
      selectedFamily === "all"
        ? "Toutes les familles"
        : families.find((item) => item.id === selectedFamily)?.label || null;

    onSelectionChange({
      axis: selectedAxis,
      branch: selectedBranch,
      family: selectedFamily,
      axisLabel,
      branchLabel,
      familyLabel,
    });
  }, [
    selectedAxis,
    selectedBranch,
    selectedFamily,
    axes,
    branches,
    families,
    onSelectionChange,
  ]);

  const selectedAxisLabel =
    selectedAxis === "all"
      ? "Vue globale"
      : axes.find((item) => item.id === selectedAxis)?.label || "—";

  const selectedBranchLabel =
    selectedBranch === "all"
      ? "Tout le périmètre"
      : branches.find((item) => item.id === selectedBranch)?.label || "—";

  const selectedFamilyLabel =
    selectedFamily === "all"
      ? "Toutes les familles"
      : families.find((item) => item.id === selectedFamily)?.label || "—";

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: theme?.spacing?.md || 14,
        padding: 6,
        boxSizing: "border-box",
      }}
    >
      {/* ============================================================
         ÉTAGE 0 — NAVIGATOR
      ============================================================ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          paddingBottom: 4,
        }}
      >
        <SectionTitle>Lecture active</SectionTitle>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <NavigatorLine label={selectedAxisLabel} level={0} />
          <NavigatorLine label={selectedBranchLabel} level={1} />
          <NavigatorLine label={selectedFamilyLabel} level={2} />
        </div>
      </div>

      {/* ============================================================
         ÉTAGE 1 — AXE D’ANALYSE
      ============================================================ */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <SectionTitle>Axe d’analyse</SectionTitle>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {axesWithAll.map((item) => {
            const active = selectedAxis === item.id;
            const tone = getAxisTone(item.tone);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedAxis(item.id)}
                style={{
                  padding: "6px 10px",
                  borderRadius: theme?.borders?.radius?.full || 9999,
                  border: active ? `1px solid ${T.corelia}` : `1px solid ${T.border}`,
                  background: active ? tone.bg : "#ffffff",
                  cursor: "pointer",
                  fontFamily: theme?.fonts?.body || "inherit",
                  fontSize: "0.77rem",
                  lineHeight: 1.1,
                  color: active ? T.corelia : tone.text,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
                {typeof item.count === "number" ? ` (${item.count})` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* ============================================================
         ÉTAGE 2 — PÉRIMÈTRE ANALYSÉ
      ============================================================ */}
      <div
        style={{
          width: "82%",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <SectionTitle>Périmètre analysé</SectionTitle>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {branchesWithAll.map((item) => {
            const active = selectedBranch === item.id;

            return (
              <SelectRow
                key={item.id}
                active={active}
                label={item.label}
                meta={item.meta}
                onClick={() => setSelectedBranch(item.id)}
              />
            );
          })}
        </div>
      </div>

      {/* ============================================================
         ÉTAGE 3 — FAMILLE ANALYSÉE
      ============================================================ */}
      <div
        style={{
          width: "82%",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <SectionTitle>Famille analysée</SectionTitle>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {familiesWithAll.map((item) => {
            const active = selectedFamily === item.id;

            return (
              <SelectRow
                key={item.id}
                active={active}
                label={item.label}
                meta={item.hint}
                onClick={() => setSelectedFamily(item.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SpecificationAnalysisDetailsPanel;